<script lang="ts">
  import { onMount, tick } from "svelte";
  import {
    TooltipPostionType,
    TOOLTIP_POSITION_CHANGE_EVENT_NAME,
  } from "./constant";

  export let content: string;
  export let triggerElement: HTMLElement;
  export let position: TooltipPostionType = "bottom";
  export let arrowSize: number = 6;
  export let distance: number = 0;
  export let backgroundStyle: string = "";
  export let textStyle: string = "";
  export let arrowColor: string = "#454450";
  export let parentSelector: string = "";
  export let notifyRemoveTooltip: boolean = false;

  let tooltipElement: HTMLElement;
  let arrowElement: HTMLElement;
  let triggerBound: DOMRect;
  let tooltipBound: DOMRect;
  let isMounted: boolean = false;

  onMount(() => {
    isMounted = true;

    document.addEventListener(
      TOOLTIP_POSITION_CHANGE_EVENT_NAME,
      updateTooltipStyle,
      true
    );

    return () => {
      document.removeEventListener(
        TOOLTIP_POSITION_CHANGE_EVENT_NAME,
        updateTooltipStyle,
        true
      );
    };
  });

  $: if (content && isMounted) {
    tooltipContentChange();
  }

  async function tooltipContentChange() {
    tooltipElement.style.maxWidth = "unset";
    await tick();
    updateTooltipStyle();
  }

  function updateTooltipStyle() {
    let pos: TooltipPostionType;
    triggerBound = triggerElement.getBoundingClientRect();
    tooltipBound = tooltipElement.getBoundingClientRect();

    if (position === "vertical") {
      const triggerTop: number = triggerBound.top;
      const triggerBottom: number = triggerBound.bottom;
      const windowHeight: number = window.innerHeight;
      pos = triggerTop > windowHeight - triggerBottom ? "top" : "bottom";
    } else if (position === "horizontal") {
      const triggerLeft: number = triggerBound.left;
      const triggerRight: number = triggerBound.right;
      const windowWidth: number = window.innerWidth;
      pos = triggerLeft > windowWidth - triggerRight ? "left" : "right";
    } else {
      pos = position;
    }

    switch (pos) {
      case "top": {
        setTooltipTop();
        break;
      }
      case "bottom": {
        setTooltipBottom();
        break;
      }
      case "left": {
        setTooltipLeft();
        break;
      }
      case "right": {
        setTooltipRight();
        break;
      }
    }
  }

  function getXLimit(): [number, number] {
    let minX: number = 0;
    let maxX: number = 1000000;
    let parentElement: HTMLElement;

    if (parentSelector) {
      parentElement = document.querySelector(parentSelector);
    }

    if (parentElement) {
      const bound = parentElement.getBoundingClientRect();
      minX = bound.left;
      maxX = bound.right;
    }

    return [minX, maxX];
  }

  function setTooltipTop() {
    const [minX, maxX] = getXLimit();
    const windowWidth = window.innerWidth;
    let xCenter = (triggerBound.left + triggerBound.right) / 2;
    xCenter = Math.min(Math.max(xCenter, minX), maxX);
    const tooltipWidth = Math.min(tooltipBound.width, windowWidth);
    const top = Math.round(
      triggerBound.top - arrowSize - tooltipBound.height - distance
    );
    const left = Math.round(
      Math.min(
        Math.max(xCenter - tooltipWidth / 2, 0),
        windowWidth - tooltipWidth
      )
    );
    const arrowLeft = xCenter - left - arrowSize;

    Object.assign(tooltipElement.style, {
      // top: top + "px",
      // left: left + "px",
      top: top + document.body.scrollTop + window.scrollY + "px",
      left: left + document.body.scrollLeft + window.scrollX + "px",
      maxWidth: tooltipWidth + "px",
    });

    Object.assign(arrowElement.style, {
      left: arrowLeft + "px",
      top: "100%",
      borderLeft: `${arrowSize}px solid transparent`,
      borderRight: `${arrowSize}px solid transparent`,
      borderTop: `${arrowSize}px solid ${arrowColor}`,
    });
  }

  function setTooltipBottom() {
    const [minX, maxX] = getXLimit();
    const windowWidth = window.innerWidth;
    let xCenter = (triggerBound.left + triggerBound.right) / 2;
    xCenter = Math.min(Math.max(xCenter, minX), maxX);
    const tooltipWidth = Math.min(tooltipBound.width, windowWidth);
    const top = Math.round(triggerBound.bottom + arrowSize + distance);
    const left = Math.round(
      Math.min(
        Math.max(xCenter - tooltipWidth / 2, 0),
        windowWidth - tooltipWidth
      )
    );
    const arrowLeft = xCenter - left - arrowSize;

    Object.assign(tooltipElement.style, {
      // top: top + "px",
      // left: left + "px",
      top: top + document.body.scrollTop + window.scrollY + "px",
      left: left + document.body.scrollLeft + window.scrollX + "px",
      maxWidth: tooltipWidth + "px",
    });

    Object.assign(arrowElement.style, {
      left: arrowLeft + "px",
      bottom: "100%",
      borderLeft: `${arrowSize}px solid transparent`,
      borderRight: `${arrowSize}px solid transparent`,
      borderBottom: `${arrowSize}px solid ${arrowColor}`,
    });
  }

  function setTooltipLeft() {
    const tooltipWidth = tooltipBound.width;
    const yCenter = (triggerBound.top + triggerBound.bottom) / 2;
    const top = yCenter - tooltipBound.height / 2;
    const left = triggerBound.left - tooltipBound.width - arrowSize - distance;
    const arrowTop = yCenter - top - arrowSize;

    Object.assign(tooltipElement.style, {
      // top: top + "px",
      // left: left + "px",
      top: top + document.body.scrollTop + window.scrollY + "px",
      left: left + document.body.scrollLeft + window.scrollX + "px",
      maxWidth: tooltipWidth + "px",
    });

    Object.assign(arrowElement.style, {
      top: arrowTop + "px",
      left: "100%",
      borderTop: `${arrowSize}px solid transparent`,
      borderBottom: `${arrowSize}px solid transparent`,
      borderLeft: `${arrowSize}px solid ${arrowColor}`,
    });
  }

  function setTooltipRight() {
    const tooltipWidth = tooltipBound.width;
    const yCenter = (triggerBound.top + triggerBound.bottom) / 2;
    const top = yCenter - tooltipBound.height / 2;
    const left = triggerBound.right + arrowSize + distance;
    const arrowTop = yCenter - top - arrowSize;

    Object.assign(tooltipElement.style, {
      // top: top + "px",
      // left: left + "px",
      top: top + document.body.scrollTop + window.scrollY + "px",
      left: left + document.body.scrollLeft + window.scrollX + "px",
      maxWidth: tooltipWidth + "px",
    });

    Object.assign(arrowElement.style, {
      top: arrowTop + "px",
      right: "100%",
      borderTop: `${arrowSize}px solid transparent`,
      borderBottom: `${arrowSize}px solid transparent`,
      borderRight: `${arrowSize}px solid ${arrowColor}`,
    });
  }
</script>

<div bind:this={tooltipElement} class="tooltip" class:remove={notifyRemoveTooltip} style={backgroundStyle}>
  <div class="whitespace-nowrap pointer-events-none" style={textStyle}>
    {content}
  </div>

  <div bind:this={arrowElement} class="absolute w-0 h-0 pointer-events-none" />
</div>

<style lang="postcss">
  .tooltip {
    @apply fixed text-center font-medium py-2 px-2.5 rounded-sm pointer-events-none;
    background-color: #454450;
    color: white;
    font-size: 12px;
    line-height: 14px;
    opacity: 1;

    /* tooltip can be showed in any components, its z-index should be the highest */
    z-index: 9999999;
  }

  .tooltip.remove {
    transition: opacity 150ms linear;
    transition-delay: 50ms;
    opacity: 0;
  }
</style>
